import dayjs from 'dayjs';
import { get, isNil } from 'lodash';
import customFunc from './util';

/**
 * 检查被保人年龄是否在限制范围内
 * @param person 被保人信息
 * @param schemeIndex 保障计划索引
 * @param professionIndex 职业索引
 * @returns 年龄是否在限制范围内
 */
export const checkPersonAgeError = ({
  person,
  schemeIndex,
  professionIndex,
  ageLimit,
  restrict,
  validStartDate,
  personText = '被保人',
}) => {
  // 根据性别取被保人年龄的限制
  let minAge, maxAge;
  const insuredIdType = get(person, 'insuredIdType') || get(person, 'cardType');
  const occupationType = get(person, 'occupationType');
  if (insuredIdType != 1 || !occupationType) {
    // 身份证类型才校验
    return null;
  }

  for (let [key, value] of ageLimit) {
    try {
      if (Array.isArray(key)) {
        // key 是数组类型说明是按职业来限制年龄
        if (key.includes(occupationType + '')) {
          minAge = value.minAge;
          maxAge = value.maxAge;
          // console.log('按职业限制年龄', occupationType, minAge, maxAge);
          break;
        }
      } else {
        // 否则按男女性别来限制年龄
        if (key == person.insuredSex) {
          minAge = value.minAge;
          maxAge = value.maxAge;
          break;
        }
      }
    } catch (error) {
      console.error('🐛 checkPersonAgeError error', error);
      return null;
    }
  }
  const birthDay = dayjs(person.birthDay);
  // 按生效日还是投保日判断年龄， 0 投保日， 1 生效日既保险起期
  const judgeDay = restrict.insureAgeCalWay === 1 ? dayjs(validStartDate) : dayjs().startOf('day');
  // 判断年龄是否在限制范围内
  if (
    // 最大年龄需要加1岁，比如最大年龄 70 周岁，则需要判断是否小于 71 周岁
    birthDay.isBefore(judgeDay.subtract(maxAge + 1, 'year')) ||
    birthDay.isSame(judgeDay.subtract(maxAge + 1, 'year')) ||
    birthDay.isAfter(judgeDay.subtract(minAge, 'year'))
  ) {
    if (isNil(schemeIndex) || isNil(professionIndex)) {
      return `被保人${person.insuredName}年龄不符合承保范围: ${minAge}-${maxAge}岁`;
    } else {
      return `保障计划${customFunc.numberToChinese(schemeIndex + 1)}的第${
        person.occupationTypeVal
      }类职业中，${personText}"${person.insuredName}"年龄超出允许范围:${minAge}-${maxAge}岁`;
    }
  }
  return null;
};

/**
 * 计算身份证校验码
 * @param {string} idCard17 身份证前17位
 * @returns {string} 校验码
 */
const calculateIdCardCheckCode = (idCard17) => {
  const weights = [7, 9, 10, 5, 8, 4, 2, 1, 6, 3, 7, 9, 10, 5, 8, 4, 2];
  const checkCodes = ['1', '0', 'X', '9', '8', '7', '6', '5', '4', '3', '2'];
  let sum = 0;
  for (let i = 0; i < 17; i++) {
    sum += parseInt(idCard17[i]) * weights[i];
  }
  return checkCodes[sum % 11];
};

/**
 * 随机生成身份证号和姓名
 * @param {number} count 生成个数
 * @returns {Array} 返回数组，每个元素包含 { idCard, name, sex, birthDay }
 */
export const generateRandomPersons = (count = 1) => {
  // 常见地区码（前6位）
  const areaCodes = [
    '110000', // 北京市
    '120000', // 天津市
    '310000', // 上海市
    '320000', // 江苏省
    '330000', // 浙江省
    '440000', // 广东省
    '510000', // 四川省
    '420000', // 湖北省
    '430000', // 湖南省
    '500000', // 重庆市
  ];

  // 常见姓氏（选一些更顺眼的）
  const surnames = [
    '王',
    '李',
    '张',
    '刘',
    '陈',
    '杨',
    '赵',
    '黄',
    '周',
    '吴',
    '郑',
    '朱',
    '何',
    '高',
    '林',
    '郭',
    '罗',
    '梁',
    '宋',
    '唐',
    '许',
    '程',
    '曹',
    '沈',
    '彭',
    '韩',
    '卢',
    '蒋',
    '蔡',
    '叶',
    '苏',
    '潘',
    '鲁',
    '杜',
    '魏',
    '江',
    '史',
    '毛',
    '顾',
    '戴',
  ];

  // 常见名字（单字，上世纪四五十年代常见风格）
  const givenNames1 = [
    '德',
    '福',
    '贵',
    '荣',
    '玉',
    '兰',
    '香',
    '萍',
    '秀',
    '珍',
    '桂',
    '英',
    '莲',
    '凤',
    '娟',
    '玲',
    '琴',
    '华',
    '芳',
    '霞',
    '明',
    '勇',
    '军',
    '刚',
    '强',
    '安',
    '平',
    '义',
    '成',
    '根',
    '忠',
    '国',
    '宏',
    '志',
    '勇',
    '兵',
    '武',
    '光',
    '庆',
    '春',
    '莲',
    '梅',
    '菊',
    '桂',
    '花',
    '珍',
    '秀',
    '燕',
    '红',
    '云',
  ];

  // 常见名字（双字，上世纪四五十年代常见组合）
  const givenNames2 = [
    '天乐',
    '小春',
    '国荣',
    '家驹',
    '铁柱',
    '二狗',
    '悟空',
    '悟能',
    '志强',
    '悟净',
    '志国',
    '杰伦',
    '志华',
    '伊健',
    '文乐',
    '大佑',
    '力宏',
    '咏麟',
    '亦菲',
    '冰冰',
    '曼玉',
    '嘉玲',
    '祖贤',
    '青霞',
    '秀兰',
    '慧乔',
    '千嬅',
    '燕姿',
    '秀莲',
    '祖儿',
    '慧琳',
    '慧敏',
    '嘉欣',
    '智霖',
    '巨基',
    '克勤',
    '千玺',
    '惠妹',
    '静茹',
    '奕迅',
    '彦祖',
    '于晏',
    '冠希',
    '德华',
    '学友',
    '富城',
    '黎明',
    '桂香',
  ];

  const result = [];

  for (let i = 0; i < count; i++) {
    // 随机选择地区码
    const areaCode = areaCodes[Math.floor(Math.random() * areaCodes.length)];

    // 生成随机生日（20-60岁之间）
    const currentYear = new Date().getFullYear();
    const minYear = currentYear - 60;
    const maxYear = currentYear - 20;
    const year = Math.floor(Math.random() * (maxYear - minYear + 1)) + minYear;
    const month = Math.floor(Math.random() * 12) + 1;
    const day = Math.floor(Math.random() * 28) + 1; // 使用28避免月份天数问题
    const birthDateStr = `${year}${String(month).padStart(2, '0')}${String(day).padStart(2, '0')}`;

    // 生成顺序码前2位（第15-16位）
    const sequenceCode = String(Math.floor(Math.random() * 99)).padStart(2, '0');

    // 生成性别码（第17位，奇数=男，偶数=女）
    // 使用同一个随机结果，既决定性别，又决定性别码
    const sexRandom = Math.random(); // 0-1
    const isMale = sexRandom > 0.5;
    const sex = isMale ? '1' : '2'; // 1=男，2=女
    const maleCodes = [1, 3, 5, 7, 9];
    const femaleCodes = [0, 2, 4, 6, 8];
    const sexCode = (isMale ? maleCodes : femaleCodes)[Math.floor(Math.random() * 5)];

    // 组合前17位：地区码(6) + 生日(8) + 顺序码(2) + 性别码(1)
    const idCard17 = areaCode + birthDateStr + sequenceCode + sexCode;

    // 计算校验码
    const checkCode = calculateIdCardCheckCode(idCard17);

    // 完整身份证号
    const idCard = idCard17 + checkCode;

    // 生成随机姓名
    const surname = surnames[Math.floor(Math.random() * surnames.length)];
    const isDoubleName = Math.random() > 0.2; // 80%概率使用双字名
    let givenName;
    if (isDoubleName) {
      givenName = givenNames2[Math.floor(Math.random() * givenNames2.length)];
    } else {
      givenName = givenNames1[Math.floor(Math.random() * givenNames1.length)];
    }
    const name = surname + givenName;

    // 格式化生日为 YYYY-MM-DD
    const birthDay = `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;

    result.push({
      insuredIdNo: idCard,
      insuredName: name,
      insuredSex: sex,
      birthDay,
      insuredIdType: '1',
    });
  }

  return result;
};



// WEBPACK FOOTER //
// ./src/util/person.js